/**
 * Определение товарных ключевых слов.
 *
 * Раньше в интерфейсе было два отдельных фильтра — «Товарні буквено-цифрові»
 * (product_alphanumeric) и «Товарні розумні» (product_smart). Они решали одну
 * задачу и по отдельности каждый терял часть товарных запросов, поэтому
 * объединены в один фильтр «Товарні ключі» (product_keys).
 *
 * Логика: запрос считается товарным, если сработал хотя бы один признак —
 *   1) слитная буквенно-цифровая комбинация (артикул/модель): iphone14, gtx1060;
 *   2) паттерн модели: WH-1000XM4, Galaxy S23, iphone 14 pro;
 *   3) бренд из справочника вместе с моделью или в короткой фразе.
 * И при этом фраза не является голой характеристикой («128gb», «2000mah»).
 *
 * При объединении исправлены три дефекта прежней реализации:
 *   - `\b` в JS считается по ASCII, поэтому слова, начинающиеся с кириллицы,
 *     не ловились вообще («айфон14» не был товаром, «iphone14» был);
 *   - бренд искался подстрокой, из-за чего «generator» находил бренд «era»,
 *     а «kingston ssd» — бренд «king»; теперь поиск идёт по границам слов;
 *   - «модель» рядом с брендом определялась как /^[A-Z]*\d+[A-Z]*$/i, что
 *     пропускало голое число: «samsung 2 шт» считалось товаром с моделью.
 *
 * Полное описание для переноса в другие сервисы — docs/product-filters-spec.md.
 */

/** Единицы измерения: «128gb», «20w», «10кг» — характеристика, а не модель. */
const MEASUREMENT_UNITS = [
  "мм", "см", "м", "км", "кг", "г", "л", "мл", "дм", "т", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "gb", "mb", "tb", "kb", "ghz", "mhz", "w", "v", "a", "ma",
  "гб", "мб", "тб", "кб", "ггц", "мгц", "вт", "в", "а", "ма", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "mah", "мач", "inch", "дюйм", "mp", "мп", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
];

/**
 * Справочник брендов по категориям.
 * Дубликаты между категориями убраны, порядок и состав сохранены.
 */
export const PRODUCT_BRANDS: string[] = [
  // Смартфони та електроніка
  "samsung",
  "apple",
  "sony",
  "lg",
  "xiaomi",
  "huawei",
  "lenovo",
  "asus",
  "dell",
  "hp",
  "canon",
  "nikon",
  "bosch",
  "philips",
  "panasonic",
  "toshiba",
  "acer",
  "msi",
  "nokia",
  "motorola",
  "oppo",
  "vivo",
  "realme",
  "oneplus",
  "honor",
  "poco",
  "redmi",
  "iphone",
  "ipad",
  "macbook",
  "imac",
  "airpods",
  "galaxy",
  "thinkpad",
  "ideapad",
  "inspiron",
  "pavilion",
  "elitebook",
  "probook",
  "zenbook",
  "vivobook",
  "tuf",
  "rog",
  "predator",
  "jbl",
  "beats",
  "bose",
  "marshall",
  "harman",
  "kardon",
  "sennheiser",
  "logitech",
  // Косметика
  "loreal",
  "maybelline",
  "nivea",
  "garnier",
  "dove",
  "gillette",
  "pantene",
  "olay",
  "vichy",
  "lancome",
  "chanel",
  "dior",
  "gucci",
  "prada",
  "versace",
  "armani",
  "ysl",
  "clinique",
  "estee",
  "lauder",
  "shiseido",
  "clarins",
  "biotherm",
  "kiehl",
  "mac",
  "nyx",
  "revlon",
  "rimmel",
  "bourjois",
  "max factor",
  "essence",
  "catrice",
  "oriflame",
  "avon",
  "mary kay",
  "faberlic",
  "natura siberica",
  "черный жемчуг", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  // Побутова техніка
  "electrolux",
  "whirlpool",
  "zanussi",
  "indesit",
  "ariston",
  "beko",
  "gorenje",
  "siemens",
  "miele",
  "aeg",
  "candy",
  "hotpoint",
  "haier",
  "midea",
  "gree",
  "daikin",
  "mitsubishi",
  "fujitsu",
  "delonghi",
  "krups",
  "moulinex",
  "tefal",
  "braun",
  "kenwood",
  "kitchenaid",
  "vitamix",
  "ninja",
  "instant pot",
  "redmond",
  "polaris",
  "scarlett",
  "vitek",
  "saturn",
  "zelmer",
  "dyson",
  "karcher",
  "thomas",
  // Аптека та препарати
  "pfizer",
  "bayer",
  "novartis",
  "roche",
  "sanofi",
  "johnson",
  "glaxo",
  "astrazeneca",
  "abbott",
  "merck",
  "teva",
  "sandoz",
  "gedeon",
  "richter",
  "дарниця", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "артеріум", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "фармак", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "nurofen",
  "panadol",
  "strepsils",
  "hexoral",
  "tantum",
  "verde",
  "aspirin",
  "ibuprofen",
  "paracetamol",
  "диклофенак", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "німесулід", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "мефенамінка", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "accu-chek",
  "one touch",
  "microlife",
  "omron",
  "beurer",
  "medisana",
  "hartmann",
  // Меблі
  "ikea",
  "jysk",
  "blum",
  "hettich",
  "egger",
  "kronospan",
  "swiss krono",
  "hafele",
  "king koil",
  "serta",
  "tempur",
  "sealy",
  "simmons",
  "natuzzi",
  "ashley",
  "lazyboy",
  "новий стиль", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "меблі прогрес", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "svit mebliv",
  "embawood",
  "forte",
  "black red white",
  // Інструменти
  "makita",
  "dewalt",
  "milwaukee",
  "hilti",
  "metabo",
  "festool",
  "ryobi",
  "black decker",
  "stanley",
  "stihl",
  "husqvarna",
  "dremel",
  "skil",
  "einhell",
  "parkside",
  "dnipro-m",
  "інтерскол", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "енергомаш", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "витязь", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "зеніт", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "stern",
  "sparky",
  "crown",
  // Будівництво
  "knauf",
  "ceresit",
  "henkel",
  "sika",
  "basf",
  "mapei",
  "weber",
  "baumit",
  "caparol",
  "rockwool",
  "isover",
  "ursa",
  "технониколь", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "ruukki",
  "tegola",
  "onduvilla",
  "fakro",
  "velux",
  "rehau",
  "veka",
  "salamander",
  "aluplast",
  "kbe",
  "tikkurila",
  "dulux",
  "sniezka",
  "alpina",
  "sadolin",
  "pinotex",
  "belinka",
  "remmers",
  // Одяг та взуття
  "nike",
  "adidas",
  "puma",
  "reebok",
  "new balance",
  "asics",
  "saucony",
  "under armour",
  "north face",
  "columbia",
  "patagonia",
  "mammut",
  "salomon",
  "merrell",
  "timberland",
  "zara",
  "h&m",
  "mango",
  "massimo dutti",
  "uniqlo",
  "gap",
  "levis",
  "wrangler",
  "lee",
  "calvin klein",
  "tommy hilfiger",
  "ralph lauren",
  "lacoste",
  "hugo boss",
  "dolce gabbana",
  "balenciaga",
  "burberry",
  "fendi",
  "ecco",
  "geox",
  "clarks",
  "skechers",
  "crocs",
  "birkenstock",
  "dr martens",
  "vans",
  "converse",
  "keds",
  "toms",
  "havaianas",
  "ipanema",
  "rider",
  "grendha",
  // Алкоголь
  "hennessy",
  "martell",
  "remy martin",
  "courvoisier",
  "camus",
  "johnnie walker",
  "jack daniels",
  "jameson",
  "chivas",
  "ballantines",
  "grants",
  "dewar",
  "glenfiddich",
  "macallan",
  "glenlivet",
  "highland park",
  "ardbeg",
  "lagavulin",
  "talisker",
  "absolut",
  "grey goose",
  "belvedere",
  "finlandia",
  "smirnoff",
  "nemiroff",
  "хортиця", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "moet",
  "chandon",
  "veuve clicquot",
  "dom perignon",
  "cristal",
  "krug",
  "bollinger",
  "corona",
  "heineken",
  "carlsberg",
  "stella artois",
  "budweiser",
  "guinness",
  "hoegaarden",
  // Продукти харчування
  "nestle",
  "danone",
  "activia",
  "actimel",
  "растішка", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "president",
  "viola",
  "ferrero",
  "lindt",
  "milka",
  "ritter sport",
  "toblerone",
  "kinder",
  "nutella",
  "barilla",
  "buitoni",
  "knorr",
  "maggi",
  "heinz",
  "hellmann",
  "calve",
  "торчин", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "чумак", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "верес", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "bonduelle",
  "hame",
  "alpro",
  "vici",
  "norven",
  "водний світ", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "pepsi",
  "coca-cola",
  "sprite",
  "fanta",
  "schweppes",
  "lipton",
  "nestea",
  "fuzetea",
  "nescafe",
  "jacobs",
  "carte noire",
  "lavazza",
  "illy",
  "julius meinl",
  "davidoff",
  "ahmad",
  "greenfield",
  "tess",
  "dilmah",
  "twinings",
  "richard",
  "basilur",
  // Дитячі товари
  "pampers",
  "huggies",
  "libero",
  "merries",
  "moony",
  "goon",
  "chicco",
  "avent",
  "medela",
  "tommee tippee",
  "nuby",
  "canpol",
  "baby bjorn",
  "stokke",
  "cybex",
  "maxi-cosi",
  "britax",
  "recaro",
  "joie",
  "graco",
  "safety 1st",
  "hauck",
  "cam",
  "lego",
  "playmobil",
  "fisher price",
  "mattel",
  "hasbro",
  "ravensburger",
  "clementoni",
  "mega bloks",
  "hot wheels",
  "barbie",
  "monster high",
  "littlest pet shop",
  "nerf",
  "transformers",
  "my little pony",
  "peppa pig",
  "paw patrol",
  "frozen",
  "disney",
  // Побутова хімія
  "ariel",
  "tide",
  "persil",
  "omo",
  "vanish",
  "calgon",
  "lenor",
  "vernel",
  "silan",
  "fairy",
  "finish",
  "somat",
  "calgonit",
  "bref",
  "domestos",
  "cillit",
  "duck",
  "mr muscle",
  "mr proper",
  "ajax",
  "comet",
  "cif",
  "pronto",
  "pledge",
  "glade",
  "air wick",
  "ambi pur",
  "dr beckmann",
  "фрекен бок", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "sama",
  "санокс", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "білизна", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  // Автозапчастини та автотовари
  "denso",
  "ngk",
  "champion",
  "mann",
  "filter",
  "mahle",
  "knecht",
  "wix",
  "sachs",
  "luk",
  "valeo",
  "brembo",
  "ate",
  "trw",
  "ferodo",
  "textar",
  "jurid",
  "monroe",
  "kyb",
  "bilstein",
  "koni",
  "continental",
  "michelin",
  "bridgestone",
  "goodyear",
  "pirelli",
  "nokian",
  "hankook",
  "yokohama",
  "dunlop",
  "kumho",
  "castrol",
  "mobil",
  "shell",
  "total",
  "elf",
  "motul",
  "liqui moly",
  "zic",
  "idemitsu",
  "varta",
  "exide",
  "mutlu",
  "topla",
  "centra",
  "rocket",
  "atlas",
  "delkor",
  // Додаткові автозапчастини
  "gates",
  "dayco",
  "contitech",
  "hutchinson",
  "skf",
  "fag",
  "ina",
  "timken",
  "koyo",
  "ntn",
  "nsk",
  "snr",
  "optimal",
  "lemforder",
  "meyle",
  "febi",
  "swag",
  "ruville",
  "sidem",
  "moog",
  "delphi",
  "magneti marelli",
  "hella",
  "osram",
  "narva",
  "neolux",
  "koito",
  "ichikoh",
  "fte",
  "lpr",
  "metelli",
  "cifam",
  "nissens",
  "nrf",
  "ava",
  "loro",
  "van wezel",
  "prasco",
  "johns",
  "klokkerholm",
  "blic",
  "depo",
  "tyc",
  "eagle eyes",
  "sonar",
  "ксенон", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "би-ксенон", // i18n-allow-cyrillic: словник брендів/одиниць (дані)
  "led",
  // Фільтри та витратні матеріали
  "fram",
  "purflux",
  "sofima",
  "ufi",
  "blue print",
  "nipparts",
  "japanparts",
  "ashika",
  "japko",
  "herth+buss",
  "corteco",
  "elring",
  "glaser",
  "payen",
  "reinz",
  "ajusa",
  "bga",
  "freccia",
  "kolbenschmidt",
  "glyco",
  "king",
  "acl",
  "npr",
  "tpr",
  "riken",
  "hastings",
  "goetze",
  "perfect circle",
  "sealed power",
  "speed pro",
  // Гальма
  "zimmermann",
  "mintex",
  "pagid",
  "hawk",
  "ebc",
  "stoptech",
  "wilwood",
  "endless",
  "project mu",
  "dixcel",
  "winmax",
  "akebono",
  "nisshinbo",
  "advics",
  "tokico",
  "nissin",
  "sumitomo",
  "pbr",
  "remsa",
  "roadhouse",
  "dafmi",
  "nk",
  "quinton hazell",
  "brake engineering",
  "budweg",
  "frenkit",
  "autofren",
  "ert",
  // Підвіска та амортизатори
  "eibach",
  "h&r",
  "kw",
  "st suspensions",
  "ap racing",
  "ohlins",
  "tein",
  "cusco",
  "tanabe",
  "rs-r",
  "bc racing",
  "fortune auto",
  "stance",
  "air lift",
  "viair",
  "accuair",
  "airrex",
  "universal air",
  "d2 racing",
  "yellow speed",
  "feal",
  "silvers",
  "gaz",
  "spax",
  "weitec",
  "fk automotive",
  "jom",
  "ta technix",
  // Електрика та електроніка
  "vdo",
  "jaeger",
  "facet",
  "intermotor",
  "standard",
  "wells",
  "dorman",
  "acdelco",
  "motorcraft",
  "mopar",
  "genuine",
  "hitachi",
  "mitsubishi electric",
  "lucas",
  "ducellier",
  "femsa",
  "delco remy",
  "iskra",
  "letrika",
  "cargo",
  "hc-cargo",
  "as-pl",
  "casco",
  "sando",
  "messmer",
  "era",
  "meat&doria",
  "hoffer",
  // Автоакустика та мультимедіа
  "pioneer",
  "alpine",
  "jvc",
  "clarion",
  "blaupunkt",
  "nakamichi",
  "focal",
  "hertz",
  "audison",
  "morel",
  "rainbow",
  "eton",
  "dynaudio",
  "canton",
  "mb quart",
  "polk audio",
  "infinity",
  "kicker",
  "rockford fosgate",
  "mtx",
  "prology",
  "mystery",
  "soundmax",
  "supra",
  "dexp",
  "digma",
  "navitel",
  "garmin",
  "tomtom",
  "mio",
  "prestigio",
  "lexand",
  "podofo",
  "junsun",
  "android auto",
  "carplay",
  "atoto",
  "eonon",
  "joying",
  "dasaita",
  "teyes",
  "chogath",
  "ownice",
  // Сабвуфери та підсилювачі
  "ground zero",
  "steg",
  "mosconi",
  "genesis",
  "zapco",
  "arc audio",
  "mmats",
  "sundown audio",
  "dd audio",
  "fi audio",
  "crescendo",
  "deaf bonce",
  "alphard",
  "ural",
  "kicx",
  "swat",
  "calcell",
  "magnum",
  "avatar",
  "pride",
  "machete",
  "edge",
  "vibe",
  "fusion",
  "boss",
  "planet audio",
  "power acoustik",
  "crunch",
  "hifonics",
  "brutus",
  "soundstream",
  "memphis",
  "orion",
  "pyramid",
  "pyle",
  // Автохімія та догляд
  "turtle wax",
  "meguiars",
  "mothers",
  "chemical guys",
  "adams polishes",
  "griots",
  "sonax",
  "koch chemie",
  "gyeon",
  "carpro",
  "gtechniq",
  "ceramic pro",
  "opti-coat",
  "kamikaze",
  "soft99",
  "willson",
  "lavr",
  "hi-gear",
  "kerry",
  "runway",
  "aim-one",
  "grass",
  "detail",
  "menzerna",
  "rupes",
  "flex",
  "mirka",
  "3m",
  "farecla",
  "scholl concepts",
  "poorboys",
  "dodo juice",
  "autoglym",
  "simoniz",
  // Автоаксесуари
  "thule",
  "yakima",
  "rhino",
  "mont blanc",
  "lux",
  "atlant",
  "amos",
  "menabo",
  "g3",
  "kamei",
  "hapro",
  "farad",
  "modula",
  "junior",
  "inno",
  "terzo",
  "carmate",
  "weathertech",
  "husky liners",
  "3d maxpider",
  "lloyd mats",
  "intro-tech",
  "covercraft",
  "coverking",
  "caltrend",
  "fia",
  "smittybilt",
  "bestop",
  "rugged ridge",
  "quadratec",
  "teraflex",
  "rough country",
  "pro comp",
  "skyjacker",
  "rancho",
  // Тюнінг та стайлінг
  "brabus",
  "amg",
  "abt",
  "ac schnitzer",
  "hamann",
  "mansory",
  "lumma",
  "wald",
  "veilside",
  "top secret",
  "spoon sports",
  "mugen",
  "sti",
  "nismo",
  "trd",
  "mazdaspeed",
  "ralliart",
  "opc",
  "vrs",
  "abarth",
  "m performance",
  "ford performance",
  "mopar performance",
  "roush",
  "saleen",
  "shelby",
  "hennessey",
  "lingenfelter",
  "callaway",
  "ruf",
  "gemballa",
  "techart",
  "novitec",
  "prior design",
  "liberty walk",
  // Автосигналізації та безпека
  "pandora",
  "starline",
  "scher-khan",
  "cenmax",
  "leopard",
  "tomahawk",
  "alligator",
  "sheriff",
  "mongoose",
  "magic systems",
  "pandect",
  "prizrak",
  "excellent",
  "ms",
  "centurion",
  "eaglemaster",
  "black bug",
  "convoy",
  "stalker",
  "pharaon",
  "kgb",
  "viper",
  "clifford",
  "python",
  "avital",
  "autostart",
  "compustar",
  "dei",
  "audiovox",
  "code alarm",
  "prestige",
  "carbine",
  "omega",
  "meta",
  "cobra",
  "scorpion",
  "laserline",
  // Товари 18+
  "durex",
  "contex",
  "lifestyles",
  "okamoto",
  "sagami",
  "masculan",
  "vizit",
  "torex",
  "lelo",
  "womanizer",
  "satisfyer",
  "we-vibe",
  "fun factory",
  "lovense",
  "tenga",
  "fleshlight",
  "doc johnson",
  "pipedream",
  "bathmate",
  "aneros",
  "nexus",
  "rocks off",
];


/** Наибольшее число слов в многословном бренде («littlest pet shop»). */
const MAX_BRAND_WORDS = 3;

/** Односложные бренды — проверяются как целые слова, а не как подстрока. */
const SINGLE_WORD_BRANDS = new Set(
  PRODUCT_BRANDS.filter((brand) => !brand.includes(" ")).map((brand) =>
    brand.toLowerCase(),
  ),
);

/** Многословные бренды — проверяются как последовательность слов. */
const MULTI_WORD_BRANDS = new Set(
  PRODUCT_BRANDS.filter((brand) => brand.includes(" ")).map((brand) =>
    brand.toLowerCase(),
  ),
);

/**
 * Разделители токенов. Режем не только по пробелу: иначе артикул с дефисом
 * («wh-1000xm4») не пройдёт проверку слова целиком.
 */
const TOKEN_SEPARATORS = /[\s\-_/,.]+/;

/**
 * Слитная буквенно-цифровая комбинация: «iphone14», «gtx1060», «5w40».
 * Границы задаём якорями по токену, а не `\b`: в JS `\b` считается по ASCII
 * и не видит кириллицу, из-за чего «айфон14» раньше не находился.
 */
const ALPHANUMERIC_TOKEN = new RegExp(
  `^(?!\\d+(?:${MEASUREMENT_UNITS.join("|")})$)` +
    `(?:\\p{L}+\\d+[\\p{L}\\p{N}]*|\\d+\\p{L}+[\\p{L}\\p{N}]*)$`,
  "iu",
);

/** Паттерны моделей товаров. */
const MODEL_PATTERNS = [
  // Galaxy S23, iPhone 14 — регистр значим намеренно: без заглавной буквы
  // «телевізор 32» и «зарядка 20w» тоже подошли бы под «слово + число».
  /(?<![\p{L}\p{N}])\p{Lu}\p{Ll}+\s+\p{Lu}?\d{1,3}\p{L}?(?![\p{L}\p{N}])/u,
  // WH-1000XM4, GTX-1060 — буквы, дефис и обязательная цифра.
  /(?<![\p{L}\p{N}])\p{L}{2,}-[\p{L}\d]*\d[\p{L}\d]*(?![\p{L}\p{N}])/iu,
  // gtx1060, a2345, 5600xt — короткий буквенный префикс и длинный номер.
  /(?<![\p{L}\p{N}])\p{L}{1,3}\d{3,}\p{L}{0,3}(?![\p{L}\p{N}])/iu,
  // iphone 14 pro, redmi 12 ultra.
  /(?<![\p{L}\p{N}])\p{L}{2,}\s+\d+\s*(Pro|Max|Plus|Ultra|Mini|Air|SE)(?![\p{L}\p{N}])/iu,
  // MacBook 2023 — регистр значим по той же причине, что и в первом паттерне.
  /(?<![\p{L}\p{N}])\p{Lu}\p{Ll}+\s+20\d{2}(?![\p{L}\p{N}])/u,
  // артикул: XYZ-1, sku 12345.
  /(?<![\p{L}\p{N}])(model|модель|арт|артикул|sku)\s*:?\s*[\p{L}\d-]+/iu, // i18n-allow-cyrillic: словник брендів/одиниць (дані)
];

/** Голая характеристика без товара: «128gb», «2000 mah», «55 дюйм». */
const GENERIC_SPEC =
  /^\d+\s*(gb|tb|mb|гб|тб|мб|mah|мач|w|вт|inch|дюйм|mp|мп)$/iu; // i18n-allow-cyrillic: словник брендів/одиниць (дані)

/** Буквенно-цифровой токен рядом с брендом: «a52s», «5200». Голое число не в счёт. */
const BRAND_MODEL_TOKEN = /^(?=.*\d)(?=.*\p{L})[\p{L}\d]+$/iu;

function tokenize(keyword: string): string[] {
  return keyword.split(TOKEN_SEPARATORS).filter(Boolean);
}

/** Признак 1: слитная буквенно-цифровая комбинация (артикул или модель). */
export function hasAlphanumericModel(keyword: string): boolean {
  return tokenize(keyword).some((token) => ALPHANUMERIC_TOKEN.test(token));
}

/** Признак 2: паттерн модели товара. */
export function hasModelPattern(keyword: string): boolean {
  return MODEL_PATTERNS.some((pattern) => pattern.test(keyword));
}

/**
 * Признак 3: бренд из справочника.
 * Ищем по целым словам — поиск подстрокой давал ложные срабатывания
 * («generator» → бренд «era», «kingston ssd» → бренд «king»).
 */
export function hasBrand(keyword: string): boolean {
  const tokens = tokenize(keyword.toLowerCase());

  if (tokens.some((token) => SINGLE_WORD_BRANDS.has(token))) {
    return true;
  }

  if (MULTI_WORD_BRANDS.size === 0) {
    return false;
  }

  for (let size = 2; size <= MAX_BRAND_WORDS; size++) {
    for (let start = 0; start + size <= tokens.length; start++) {
      if (MULTI_WORD_BRANDS.has(tokens.slice(start, start + size).join(" "))) {
        return true;
      }
    }
  }

  return false;
}

/** Вся фраза — характеристика без товара: «128gb», «2000 mah». */
export function isGenericSpec(keyword: string): boolean {
  return GENERIC_SPEC.test(keyword.trim());
}

/**
 * Товарный ключ: артикул, модель или бренд с моделью.
 * Единый фильтр вместо прежних product_alphanumeric и product_smart.
 */
export function isProductKeyword(keyword: string): boolean {
  if (!keyword || isGenericSpec(keyword)) {
    return false;
  }

  if (hasAlphanumericModel(keyword) || hasModelPattern(keyword)) {
    return true;
  }

  if (!hasBrand(keyword)) {
    return false;
  }

  const words = keyword.split(/\s+/).filter(Boolean);

  // Бренд с моделью — товар; бренд в короткой фразе — скорее всего тоже.
  return words.some((word) => BRAND_MODEL_TOKEN.test(word)) || words.length <= 5;
}
